// Script ID for Google Maps API
const GOOGLE_MAPS_SCRIPT_ID = "google-maps-script";

// Global callback name
const CALLBACK_NAME = "googleMapsInitialized";

// Type for the callback function
type GoogleMapsCallback = (error?: Error) => void;

// Extend Window interface for the callback
declare global {
  interface Window {
    [CALLBACK_NAME]: () => void;
  }
}

// Keep track of pending callbacks, loaded state, and error state
let pendingCallbacks: GoogleMapsCallback[] = [];
let isLoaded = false;
let loadError: Error | null = null;

// Define the global callback function if running in browser environment
if (typeof window !== "undefined") {
  window[CALLBACK_NAME] = () => {
    isLoaded = true;
    const callbacks = [...pendingCallbacks];
    pendingCallbacks = [];
    callbacks.forEach((cb) => {
      try {
        cb();
      } catch (err) {
        console.error("Error executing Google Maps load callback:", err);
      }
    });
  };
}

/**
 * Loads the Google Maps API if not already loaded
 * @param apiKey Google Maps API key
 * @param callback Function to call when API is loaded (takes optional error)
 */
export function loadGoogleMapsApi(
  apiKey: string,
  callback: GoogleMapsCallback
): void {
  // Validate API key first
  if (!apiKey || typeof apiKey !== "string" || apiKey.trim() === "") {
    callback(new Error("Google Maps API key is required"));
    return;
  }

  // If already loaded, run callback immediately
  if (window.google?.maps || isLoaded) {
    callback();
    return;
  }

  // If previous load failed, propagate the error immediately
  if (loadError) {
    callback(loadError);
    return;
  }

  // Add callback to pending list
  pendingCallbacks.push(callback);

  // Check if script already exists
  const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (existingScript) {
    return;
  }

  // Create script element
  const script = document.createElement("script");
  script.id = GOOGLE_MAPS_SCRIPT_ID;
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=3&libraries=geometry&callback=${CALLBACK_NAME}`;
  script.async = true;
  script.defer = true;

  // Handle errors
  script.onerror = () => {
    const error = new Error("Google Maps script failed to load");
    loadError = error;
    console.error(error.message);
    
    const callbacks = [...pendingCallbacks];
    pendingCallbacks = [];
    callbacks.forEach((cb) => {
      try {
        cb(error);
      } catch (err) {
        console.error("Error executing Google Maps error callback:", err);
      }
    });
  };

  // Append script to document
  document.head.appendChild(script);
}

/**
 * Cleans up a specific Google Maps callback or resets state on failure
 */
export function cleanupGoogleMapsApi(callback?: GoogleMapsCallback): void {
  if (callback) {
    pendingCallbacks = pendingCallbacks.filter((cb) => cb !== callback);
  } else {
    // If no callback specified, perform fallback cleanup
    pendingCallbacks = [];
  }
}


